import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import ReactJson from "react-json-view";
import { fetchAccumulatorDetails, fetchAuthorizationById, fetchAuthorizations, fetchMemberDetailsByHccId, fetchHrpTransactionsByMemberHccId, fetchReprocessCdc, fetchMemberBySystemIdentifier, fetchMemberByMemberUuid, fetchAccAdjustHistFact } from "../../utils/apiUtils";
import MemberDetails from "./MemberDetails";
import SharedTable from "../benefitPlan/SharedTable";
import { useInView } from "react-intersection-observer";
import SimpleTable from "../shared/SimpleTable";
import Claims from "../claims/Claims";
import ToggleSwitch from "../shared/ToggleSwitch";
import HrpTable from "../dynamodb/HrpTable";
import PcpTransactionTable from "../dynamodb/PcpTransactionTable";
import Select from 'react-select';


const Member = () => {
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('')
    const params = useParams();
    const navigate = useNavigate();
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);

    const memberHccId = params.memberHccId;
    const activeTab = params.activeTab || "details";

    const {
        isPending,
        error: memberDetailError,
        data: memberData,
        status,
        error,
        isError,
        isLoading,
        isFetching,
        refetch
    } = useQuery({
        queryKey: ["memberByHccId", memberHccId],
        queryFn: () =>
            fetchMemberDetailsByHccId({
                memberHccId: memberHccId,
                memberDetailFlags: {
                    includeAccount: true,
                    includeCob: true,
                    includeHeadOfHousehold: true,
                    includeParentAccount: true,
                    includeSubAccount: true,
                    includePlan: true,
                    includeProvider: true,
                    includeProviderPhone: true,
                    includeRider: true,
                },
            }),
        enabled: !!memberHccId,
        placeholderData: keepPreviousData,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: 'always',
    });

    const { data: dynamDBMembersData, refetch: refetchDynamoDBMembersData } = useQuery({
        queryKey: ["fetchMemberBySystemIdentifier", memberHccId],
        queryFn: async ({ }) => {


            let result = await fetchMemberBySystemIdentifier({ systemIdentifier: memberHccId?.replace('-', '#') });
            if (result.Count) {
                const tasks = result.Items.map(item => fetchMemberByMemberUuid({ memberUuid: item.memberUuid }))
                const membersByUuid = await Promise.all(tasks)
                //   result = await fetchMemberByMemberUuid({ memberUuid: result.Items?.[0]?.memberUuid })
                result = { Items: membersByUuid.map(r => r.Items).flat() }
            }

            return result
        },
        enabled: !!memberHccId,
        placeholderData: keepPreviousData,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: 'always'
    });

    const reIngestMember = async ({ hccId }) => {
        try {
            setLoading(true)
            setErrorMsg('')
            await fetchReprocessCdc({
                hccId,
                typeName: 'Subscription'
            })
        } catch (err) {
            setErrorMsg(err.message)
        } finally {
            setLoading(false)
        }

    }

    const memberKey = memberData?.items?.[0]?.memberExtract?.member?.systemId;
    const subscriptionHccId = memberData?.items?.[0]?.memberExtract?.member?.subscriptionId
    const firstName = memberData?.items?.[0]?.memberExtract?.member?.firstName
    const lastName = memberData?.items?.[0]?.memberExtract?.member?.lastName
    const fullname = firstName ? firstName + ' ' + lastName : null
    const memberUuid = dynamDBMembersData?.Items?.[0]?.memberUuid
    return (
        <div className={"container "}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                    <p className="title">{fullname || 'Member'}</p>
                    <p className="sub-title">HccId: {memberHccId} | MemberUuid: {memberUuid}</p>

                    <br />
                    {loading ? <div className="loader" /> :
                        <button onClick={async () => await reIngestMember({ hccId: subscriptionHccId })}> Re ingest</button>
                    }

                    {errorMsg && <p className="danger">{errorMsg}</p>}


                </div>

                <div>
                    <ToggleSwitch label={`Auto Refresh`} onToggle={(val) => setAutoRefreshEnabled(val)} initialState={autoRefreshEnabled} />
                </div>
            </div>

            {status === "loading" || isLoading || isFetching ? <> <br /><div className="loader" /> </> : status === "error" || isError ? <span className="danger">Error: {error.message}</span> : <></>}
            {memberData && (
                <>
                    {/* <ReactJson
                    style={{ padding: "1rem" }}
                    src={memberData}
                    name={false}
                    collapsed={3}
                    collapseStringsAfterLength={150}
                    displayDataTypes={false}
                    displayObjectSize={false}
                    sortKeys={false}
                /> */}
                    <div className="tabs">
                        {["details", "accum", "authorization", "claims", "transactions", "pcpTransactions", "ingestion"].map((tab) => (
                            <div className={"tab" + (activeTab === tab ? " active-tab" : "")} key={tab} onClick={() => navigate(`/members/${memberHccId}/${tab}`)}>
                                {tab}
                            </div>
                        ))}
                    </div>

                    {activeTab === "details" ? (
                        <MemberDetails data={memberData} dynamDBMembersData={dynamDBMembersData} autoRefreshEnabled={autoRefreshEnabled} refetch={async () => {
                            await Promise.all([
                                refetch(), refetchDynamoDBMembersData()
                            ])
                        }} />
                    ) : activeTab === "accum" ? (
                        <Accumulators memberHccId={memberHccId} memberKey={memberKey} />
                    ) : activeTab === "claims" ? (
                        <Claims memberKey={memberKey} />
                    ) : activeTab === "authorization" ? (
                        <Authorizations memberKey={memberKey} />
                    ) : activeTab === "transactions" ? (
                        <Transactions memberHccId={memberHccId} autoRefreshEnabled={autoRefreshEnabled} />
                    ) :
                        activeTab === "ingestion" ? (
                            <HrpTable subscriptionHccId={subscriptionHccId} autoRefreshEnabled={autoRefreshEnabled} />
                        ) : memberUuid && activeTab === "pcpTransactions" ?
                            <PcpTransactionTable memberUuid={memberUuid} />
                            : (
                                <div>not tab selected</div>
                            )}
                </>
            )}
        </div>
    );
};

export default Member;

const transactionTypeOptions = [
    { label: 'PcpSelection', value: 'PcpSelection' },
    { label: 'Referral', value: 'Referral' }
]

const Transactions = ({ memberHccId, autoRefreshEnabled }) => {
    const { ref, inView } = useInView();
    const [transactionType, setTransactionType] = useState()

    const queryKey = ["transactions", memberHccId];

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error,
    } = useInfiniteQuery({
        retry: false,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { memberHccId, lastEvaluatedKey: pageParam };

            return fetchHrpTransactionsByMemberHccId(params);
        },
        select: (data) => {
            return {
                pages: data.pages.flatMap((x) => x.Items),
                pageParams: data.pageParams,
            };
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null;
        },
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: 'always',
        staleTime: 0, //,Infinity,
        enabled: true, //&& !!showChat
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const items = data?.pages.flat();
    let columns = [
        "transactionId",
        "transactionType",
        "memberUuid",
        "memberHccId",
        "transactionDate",
        "status"];


    if (transactionType?.value === 'PcpSelection') {
        columns = [...columns,
            "OldImage.pcpChosenBy",
            "OldImage.pcpNpi",
            "OldImage.pcpFirstName",
            "OldImage.pcpLastName",
            "OldImage.pcpPracticeName",
            "NewImage.pcpChosenBy",
            "NewImage.pcpNpi",
            "NewImage.pcpFirstName",
            "NewImage.pcpLastName",
            "NewImage.pcpPracticeName"]
    }
    else if (transactionType?.value === 'Referral') {
        columns = [...columns,
            "NewImage.referralUuid",
            "NewImage.referralSpecialty",
            "NewImage.referredByPcpNpi",
            "NewImage.referredByPcpFirstName",
            "NewImage.referredByPcpLastName",
            "NewImage.referredByPcpPracticeName",
            "NewImage.referredToPcpNpi",
            "NewImage.referredToPcpFirstName",
            "NewImage.referredToPcpLastName",
            "NewImage.referredToPcpPracticeName"
        ]
    }



    return (
        <>

            <Select
                name={'transactionType'}
                className="react-select-container large-input"
                classNamePrefix="react-select"
                placeholder={'Columns by Transaction Type'}
                // inputRef={field.ref}
                options={transactionTypeOptions}
                value={transactionType} // Pass the state variable here
                onChange={(option) => { setTransactionType(option) }}
            // value={options.find((c) => c.value === field.value)}
            // onChange={(val) => field.onChange(val.value)}
            />

            <br />
            <SharedTable
                type={"transactions"}
                columns={columns}
                items={items}
                activeDraftId={null}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                isLoading={isLoading}
                // onDetailHandler={onDetailHandler}
                ref={ref}
            />
        </>

    );
};

const Accumulators = ({ memberHccId, memberKey }) => {
    const [activeSwtich, setActiveSwitch] = useState('overview'); //kanban backlog

    const { ref, inView } = useInView()


    const {
        error: accumError,
        data: accumData,
        status,
        isError,
        error,
    } = useQuery({
        queryKey: ["memberAccum", memberHccId],
        queryFn: () => fetchAccumulatorDetails({ memberHccId, benefitPlanType: "Medical" }),
        enabled: !!memberHccId,
    });
    const limit = 20
    const {
        data: accumAdjustHistFactData,
        status: accumAdjustHistFactStatus,
        isError: accumAdjustHistFactIsError,
        error: accumAdjustHistFactError,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ["getAccAdjustHistFact", memberKey],
        queryFn: ({ pageParam = {} }) => fetchAccAdjustHistFact({
            ...pageParam,
            limit,
            "memberKey": memberKey
        }),

        select: (data) => {
            //   console.log({ data });
            return {
                count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows.map(row => ({
                    ...row
                }))).flatMap((x) => x)
            };
        },
        initialPageParam: 0,
        placeholderData: (previousData, previousQuery) => previousData,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage.rows?.length === limit

            if (!hasMore) return undefined
            return {
                offset: pages.reduce((accum, page) => {
                    return accum + page.rows.length;
                }, 0),
            };
        },
        staleTime: 0,
        enabled: !!memberKey && activeSwtich === 'history',
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage()
        }
    }, [inView])


    return (
        <div style={{ overflowY: 'scroll' }}>
            {/* <p className="title">Accumulator Details</p>
            <br /> */}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '1rem 0rem' }}>
                <div className="switch" >
                    <p className={`option ${activeSwtich === "overview" ? "active" : ""}`}
                        onClick={() => setActiveSwitch("overview")}>
                        Overview
                    </p>
                    <p className={`option ${activeSwtich === "history" ? "active" : ""}`} onClick={() => setActiveSwitch("history")}>
                        History
                    </p>

                </div>



            </div>

            {status === "loading" ? <p>Loading...</p> : status === "error" || isError ? <span className="danger">Error: {error.message}</span> : <></>}

            {activeSwtich === 'history' ? <>          {accumAdjustHistFactError === 'loading' ? (
                <p>Loading...</p>
            ) : accumAdjustHistFactError === 'error' ? (
                <span>Error: {accumAdjustHistFactError.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={'accAdjust'}
                        columns={['accAdjustStartDate', 'accAdjustAmount', 'accumulatorAdjustSource.accAdjustSourceName', 'provisionLabel', 'accAdjustDesc']}
                        items={accumAdjustHistFactData?.rows}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        onDetailHandler={() => { }}
                        ref={ref}
                    />

                </>)}</> : <>           {accumData?.items?.map((item, i) => {
                    return (
                        <React.Fragment key={i}>
                            <p>{item?.benefitPlanAccum?.planName}</p>
                            <p>{item?.benefitPlanAccum?.enrolledPlanHccId}</p>

                            {item?.status && <p className="danger">{`${item?.status}: ${item?.errorMessage}`}</p>}

                            <p className="sub-title" style={{ padding: "1rem 0rem .4rem 0rem" }}>
                                Accumulator
                            </p>
                            <SimpleTable
                                key={`accun ${i}`}
                                columns={["tier", "accumulatorType", "benefitDesc", "defined", "used", "remaining", "startDate", "endDate", "isFamily", "provisionLabel", "provisionHicl"].map(
                                    (k) => ({ label: k, attribute: k })
                                )}
                                rows={item?.benefitPlanAccum?.accumulators?.sort((a, b) => a.tier?.localeCompare(b.tier))}
                            />

                            <p className="sub-title" style={{ padding: "1rem 0rem .4rem 0rem" }}>
                                Benefit Quotas
                            </p>

                            <SimpleTable
                                key={`benefitQuotas ${i}`}
                                disableHeaderFormatting={true}
                                columns={["tier", "accumulatorType", "benefitDesc", "defined", "used", "remaining", "startDate", "endDate", "isFamily", "provisionLabel", "provisionHicl"].map(
                                    (k) => ({ label: k, attribute: k })
                                )}
                                rows={item?.benefitPlanAccum?.accumulators}
                            />
                        </React.Fragment>
                    );
                })}</>}



        </div>
    );
};

const Authorizations = ({ memberKey }) => {
    const { ref, inView } = useInView();

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const limit = 10;
    const {
        data: authorizationData,
        error,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
        status,
        refetch,
    } = useInfiniteQuery({
        queryKey: ["memberAuth", limit, memberKey],
        queryFn: async ({ pageParam }) => {
            let params = pageParam || {};
            return await fetchAuthorizations({ ...params, limit, memberKey });
        },

        select: (data) => {
            // console.log({ data });
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const hasMore = lastPage?.rows?.length === limit;

            if (!hasMore) return undefined;
            return {
                offset: pages.reduce((accum, page) => {
                    return accum + page.rows.length;
                }, 0),
            };
        },
        staleTime: 0,
        enabled: !!memberKey,
    });

    return (
        <SharedTable
            type={"authorizations"}
            columns={["authHccId", "authExternalId", "receivedOnTime", "authReferredFrom.referredFromHccId", "authReferredFrom.referredFromName"]}
            items={authorizationData?.rows}
            activeDraftId={null}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            isFetching={isFetching}
            isLoading={isLoading}
            // onEditHandler={onEditHandler}
            onDetailHandler={async (body) => {
                const authDetails = await fetchAuthorizationById({ authorizationId: body.authHccId });
                return authDetails;
            }}
            disableModel={false}
            ref={ref}
        />
    );
};
